import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx'; // Standalone Excel Parser
import { db } from './firebase'; 
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, getDocs, addDoc, serverTimestamp, query, where, writeBatch } from 'firebase/firestore';
import Settings from './Settings';

// NEW: Accept isPWA prop
function MainApp({ user, isPWA, handleLogout }) {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('menu'); 
  
  // --- PWA INSTALLATION STATE ---
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // --- DASHBOARD & NAVIGATION STATE ---
  const [activeTab, setActiveTab] = useState('WW RECORD');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [dashboardSubject, setDashboardSubject] = useState('');
  const [dashboardGrades, setDashboardGrades] = useState([]);
  const [dashboardAttendance, setDashboardAttendance] = useState([]); 

  // --- SETUP & STUDENT DATA ---
  const [subjects, setSubjects] = useState([]);
  const [allRawStudents, setAllRawStudents] = useState([]); 
  
  // --- MANUAL SUBJECT ENTRY STATES ---
  const [manualSubject, setManualSubject] = useState('');
  const [manualGrade, setManualGrade] = useState('');
  const [manualSection, setManualSection] = useState('');

  const standardSubjects = [
    "MATH", "SCIENCE", "ENGLISH", "FILIPINO", "ARALING PANLIPUNAN", 
    "MAPEH", "TLE", "EPP", "ESP", "VALUES EDUCATION", 
    "HOMEROOM GUIDANCE", "MOTHER TONGUE", "RESEARCH"
  ];
  
  // --- STANDALONE EXCEL UPLOAD STATES ---
  const [excelGrade, setExcelGrade] = useState('');
  const [excelSection, setExcelSection] = useState('');
  
  const initialSetupState = { isLicensed: true, licensee: '', email: '', region: '', division: '', district: '', schoolYear: '', wwWeight: '', ptWeight: '', qaWeight: '', schoolId: '', schoolName: '', teacherName: '', address: '', subjects: Array(14).fill('') };
  const [setupData, setSetupData] = useState(initialSetupState);
  const fileInputRef = useRef(null);

  // --- GRADING, RAPID ENTRY, & ATTENDANCE STATE ---
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('WW'); 
  const [selectedStudent, setSelectedStudent] = useState('');
  const [rapidTaskIndex, setRapidTaskIndex] = useState(0);
  const [rapidHps, setRapidHps] = useState('');
  const [rapidScore, setRapidScore] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [editRecords, setEditRecords] = useState([]);
  const [editAttendanceStats, setEditAttendanceStats] = useState(null); 

  // --- ATTENDANCE SPECIFIC ---
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [searchAttStudent, setSearchAttStudent] = useState('');
  const [searchAttSubject, setSearchAttSubject] = useState('');
  const [attStats, setAttStats] = useState(null);

  // --- REPORTS SPECIFIC ---
  const [reportType, setReportType] = useState('class'); 
  const [reportQuarter, setReportQuarter] = useState('Q1');
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().split('-')[1]); 
  const [reportSubject, setReportSubject] = useState('');
  const [reportStudent, setReportStudent] = useState('');
  const [reportData, setReportData] = useState(null); 
  const [studentReportData, setStudentReportData] = useState(null); 

  // Month Array for SF 2
  const monthOptions = [
    { val: '01', label: 'January' }, { val: '02', label: 'February' }, { val: '03', label: 'March' },
    { val: '04', label: 'April' }, { val: '05', label: 'May' }, { val: '06', label: 'June' },
    { val: '07', label: 'July' }, { val: '08', label: 'August' }, { val: '09', label: 'September' },
    { val: '10', label: 'October' }, { val: '11', label: 'November' }, { val: '12', label: 'December' }
  ];

  // --- NEW: PWA INSTALL LISTENER ---
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // --- HELPER: GET CURRENT DATE STRING ---
  const getCurrentDateStr = () => {
    const d = new Date();
    const months = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
    const days = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    return `${months[d.getMonth()]} ${d.getDate()}, ${days[d.getDay()]}`;
  };

  const isHeader = (name) => {
    return name === 'MALE' || name === 'FEMALE' || name === 'ALL STUDENTS' || name.startsWith('GRADE ') || name === 'UNASSIGNED CLASS';
  };

  const getStudentListForSubject = (subjectName) => {
    if (!subjectName) return [];
    
    let targetGrade = null;
    let targetSection = null;
    const match = subjectName.match(/- GR (.+?)\s+(.+)/);
    
    if (match) {
      targetGrade = match[1].trim().toUpperCase();
      targetSection = match[2].trim().toUpperCase();
    }

    let filteredRaw = allRawStudents;
    if (targetGrade && targetSection) {
       filteredRaw = allRawStudents.filter(s => 
         String(s.grade).trim().toUpperCase() === targetGrade && 
         String(s.section).trim().toUpperCase() === targetSection
       );
    }

    const grouped = { males: [], females: [] };
    
    const formatName = (d) => {
      const mi = d.middleName ? ` ${d.middleName.charAt(0)}.` : '';
      return `${d.lastName}, ${d.firstName}${mi}`;
    };

    filteredRaw.forEach(s => {
      if (s.gender === 'Male' || s.gender?.toUpperCase().startsWith('M')) {
        grouped.males.push(formatName(s));
      } else {
        grouped.females.push(formatName(s));
      }
    });

    const formattedList = [];
    if (targetGrade && targetSection) formattedList.push(`GRADE ${targetGrade} - ${targetSection}`);
    else formattedList.push(`ALL STUDENTS`);

    const males = grouped.males.sort((a, b) => a.localeCompare(b));
    const females = grouped.females.sort((a, b) => a.localeCompare(b));

    if (males.length > 0) {
      formattedList.push("MALE");
      males.forEach(m => formattedList.push(m));
    }
    if (females.length > 0) {
      formattedList.push("FEMALE");
      females.forEach(f => formattedList.push(f));
    }

    return formattedList;
  };

  const getGroupedRegistryList = () => {
    const grouped = {};
    
    const formatName = (d) => {
      const mi = d.middleName ? ` ${d.middleName.charAt(0)}.` : '';
      return `${d.lastName}, ${d.firstName}${mi}`;
    };

    allRawStudents.forEach(s => {
      const key = (s.grade && s.section) ? `GRADE ${s.grade} - ${s.section}` : 'UNASSIGNED CLASS';
      if (!grouped[key]) grouped[key] = { males: [], females: [] };
      if (s.gender === 'Male' || s.gender?.toUpperCase().startsWith('M')) grouped[key].males.push(formatName(s));
      else grouped[key].females.push(formatName(s));
    });

    const formattedList = [];
    const sortedClasses = Object.keys(grouped).sort();

    sortedClasses.forEach(cls => {
      formattedList.push(cls);
      const males = grouped[cls].males.sort((a, b) => a.localeCompare(b));
      const females = grouped[cls].females.sort((a, b) => a.localeCompare(b));
      if (males.length > 0) { formattedList.push("MALE"); males.forEach(m => formattedList.push(m)); }
      if (females.length > 0) { formattedList.push("FEMALE"); females.forEach(f => formattedList.push(f)); }
    });
    return formattedList;
  };

  const autoPruneOldRecords = async (currentYear) => {
    if (!currentYear) return;
    try {
      const studentsRef = collection(db, "users", user.uid, "students");
      const snap = await getDocs(studentsRef);
      const uniqueYears = new Set();
      snap.forEach(d => { if (d.data().schoolYear) uniqueYears.add(d.data().schoolYear); });
      const sortedYears = Array.from(uniqueYears).sort(); 
      
      if (sortedYears.length > 3) {
        const oldestYear = sortedYears[0];
        const batch = writeBatch(db);
        let batchCount = 0;

        const collectionsToPrune = ["students", "grades", "attendance"];
        for (const colName of collectionsToPrune) {
           const q = query(collection(db, "users", user.uid, colName), where("schoolYear", "==", oldestYear));
           const oldDocs = await getDocs(q);
           batchCount += oldDocs.size; // Safely count before the loop to fix Linter Warning
           oldDocs.forEach(d => batch.delete(d.ref));
        }
        if (batchCount > 0) await batch.commit();
      }
    } catch (e) { console.error("Error auto-pruning:", e); }
  };

  const fetchAndSortStudents = async (activeYear) => {
    if (!activeYear) return;
    try {
      const studentsRef = collection(db, "users", user.uid, "students");
      const q = query(studentsRef, where("schoolYear", "==", activeYear));
      const studentSnap = await getDocs(q);
      setAllRawStudents(studentSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) { console.error("Error fetching students:", error); }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        let currentYear = initialSetupState.schoolYear;

        if (userSnap.exists() && userSnap.data().setupConfig) {
          const cloudSetup = userSnap.data().setupConfig;
          currentYear = cloudSetup.schoolYear;
          setSetupData({ ...initialSetupState, ...cloudSetup });
          const loadedSubjects = cloudSetup.subjects?.filter(s => s !== "") || [];
          setSubjects(loadedSubjects);
          if (loadedSubjects.length > 0) {
            setDashboardSubject(loadedSubjects[0]);
            setSelectedSubject(loadedSubjects[0]);
            setReportSubject(loadedSubjects[0]);
          }
        } else {
          await setDoc(userRef, { setupConfig: initialSetupState }, { merge: true });
        }
        
        await fetchAndSortStudents(currentYear);
        if (currentYear) autoPruneOldRecords(currentYear);

      } catch (err) { console.error("Firestore Load Error:", err); } 
      finally { setLoading(false); }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (view === 'menu' && dashboardSubject && setupData.schoolYear) {
      const fetchDashboardData = async () => {
        const gradesRef = collection(db, "users", user.uid, "grades");
        const qGrades = query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("quarter", "==", selectedQuarter), where("subject", "==", dashboardSubject));
        const snapGrades = await getDocs(qGrades);
        setDashboardGrades(snapGrades.docs.map(doc => doc.data()));

        const attRef = collection(db, "users", user.uid, "attendance");
        const qAtt = query(attRef, where("schoolYear", "==", setupData.schoolYear), where("quarter", "==", selectedQuarter), where("subject", "==", dashboardSubject));
        const snapAtt = await getDocs(qAtt);
        setDashboardAttendance(snapAtt.docs.map(doc => doc.data()));
      };
      fetchDashboardData();
    }
  }, [view, selectedQuarter, dashboardSubject, user.uid, setupData.schoolYear]);

  useEffect(() => {
    if (view === 'attendance_daily' && selectedSubject) {
      const list = getStudentListForSubject(selectedSubject);
      setAttendanceList(list.map(s => ({ student: s, status: isHeader(s) ? null : 'Present', isHeaderRow: isHeader(s) })));
    } else if (view === 'attendance_daily' && !selectedSubject) {
      setAttendanceList([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedSubject, allRawStudents]);

  const goHome = () => {
    setView('menu'); setSaveMessage(''); setSelectedStudent(''); setEditRecords([]); setEditAttendanceStats(null); setAttStats(null); setReportData(null); setStudentReportData(null);
  };

  const handleStudentDoubleClick = (studentName) => {
    if (isHeader(studentName)) return;
    setSelectedStudent(studentName);
    const subjectToUse = (view === 'register') ? dashboardSubject : (view === 'menu' ? dashboardSubject : selectedSubject);
    setSelectedSubject(subjectToUse);
    setView('edit_scores');
    loadRecordsForStudent(studentName, subjectToUse); 
  };

  const handleSaveSetup = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { setupConfig: setupData, updatedAt: serverTimestamp() });
      setSubjects(setupData.subjects.filter(s => s !== ""));
      alert("✅ Setup Saved to Cloud!");
      await fetchAndSortStudents(setupData.schoolYear);
      autoPruneOldRecords(setupData.schoolYear);
      setView('menu');
    } catch (err) { alert("❌ Save Failed: " + err.message); } 
    finally { setLoading(false); }
  };

  // --- REVISED FACTORY RESET FOR NEW SCHOOL YEAR ---
  const handleFactoryReset = async () => {
    const confirmText = prompt('⚠️ DANGER ZONE: This will permanently delete ALL your students, grades, attendance data, and classes.\n\nType "RESET" to confirm:');
    if (confirmText !== 'RESET') return;
    setLoading(true);
    try {
      // 1. Delete all the heavy data collections (students, grades, attendance)
      const collectionsToShred = ["students", "grades", "attendance"];
      for (const colName of collectionsToShred) {
         const snap = await getDocs(collection(db, "users", user.uid, colName));
         await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
      }

      // 2. Keep the user's profile info safe, but clear the school year and subjects
      const preservedSetup = {
        ...setupData,
        schoolYear: '', // Clears the Active School Year as requested
        subjects: Array(14).fill('') // Clears the subjects handled by the teacher
      };

      await updateDoc(doc(db, "users", user.uid), { setupConfig: preservedSetup });
      
      alert("✅ Data has been successfully cleared for the new year! Your school and teacher profile were kept safe.");
      window.location.reload(); 
    } catch (err) { 
      alert("❌ Error resetting app."); 
      console.error(err); 
    } finally { 
      setLoading(false); 
    }
  };

  // --- NEW: PWA INSTALL HANDLER ---
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
      } else {
        console.log('❌ User dismissed the install prompt');
      }
      // We've used the prompt, and can't use it again, throw it away
      setDeferredPrompt(null);
    }
  };

  // --- MANUAL SUBJECT ENTRY ---
  const handleAddManualSubject = async () => {
    if (!manualSubject || !manualGrade || !manualSection) {
      alert("⚠️ Please select a Subject, Grade, and Section.");
      return;
    }

    const newSubName = `${manualSubject.toUpperCase()} - GR ${manualGrade.toUpperCase().trim()} ${manualSection.toUpperCase().trim()}`;
    
    let currentSubs = [...setupData.subjects].filter(s => s.trim() !== "");
    if (currentSubs.includes(newSubName)) {
      alert("⚠️ This exact class is already in your official load!");
      return;
    }

    setLoading(true);
    currentSubs.push(newSubName);
    
    // Ensure the array always meets the old 14-item minimum requirement to prevent crashes
    const paddedSubs = [...currentSubs];
    while (paddedSubs.length < 14) paddedSubs.push(""); 

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { "setupConfig.subjects": paddedSubs, updatedAt: serverTimestamp() });
      
      setSetupData(prev => ({ ...prev, subjects: paddedSubs }));
      setSubjects(currentSubs);
      
      // Automatically set standard dashboard subjects if this is their first class
      if (currentSubs.length === 1 && !dashboardSubject) {
        setDashboardSubject(currentSubs[0]);
        setSelectedSubject(currentSubs[0]);
        setReportSubject(currentSubs[0]);
      }
      
      // Clear inputs
      setManualSubject('');
      setManualGrade('');
      setManualSection('');
      
      alert(`✅ Successfully added ${newSubName} to your Official Class Load.`);
    } catch (error) {
      console.error("Error adding subject:", error);
      alert("❌ Failed to add subject. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // --- EXCEL SMART PARSER ---
  const handleImportMasterRoster = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!setupData.schoolYear) {
      alert("⚠️ Please set your School Year in the Settings tab first.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (!excelGrade || !excelSection) {
      alert("⚠️ Please enter the Grade and Section before selecting the file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setLoading(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        
        // Read the file as a raw 2D Array to bypass all messy formatting and headers
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (rawRows.length === 0) {
          alert("❌ The Excel file looks empty. Please check your file.");
          setLoading(false);
          return;
        }

        const batch = writeBatch(db);
        const studentsRef = collection(db, "users", user.uid, "students");

        // Grab existing students to check for duplicates using their LRN
        const existingDocs = await getDocs(query(studentsRef, where("schoolYear", "==", setupData.schoolYear)));
        const existingMap = {};
        existingDocs.forEach(d => { 
            if (d.data().lrn) existingMap[d.data().lrn] = d.ref; 
        });

        let addedCount = 0; 
        let updatedCount = 0;

        // Scan every single row in the Excel file
        rawRows.forEach((row) => {
          let lrn = null;
          let lrnColIndex = -1;
          
          // 1. Look for a 12-digit LRN in any cell to confirm this is an actual student row
          for (let i = 0; i < row.length; i++) {
            const cellVal = row[i] ? String(row[i]).replace(/\s+/g, "") : "";
            if (/^\d{12}$/.test(cellVal)) {
              lrn = cellVal;
              lrnColIndex = i;
              break; 
            }
          }

          // 2. If an LRN was found, dynamically extract the next 4 pieces of data
          if (lrn) {
             // We slice the row after the LRN and remove any blank/merged cells
             const validDataCells = row.slice(lrnColIndex + 1).filter(cell => cell !== undefined && cell !== null && String(cell).trim() !== "");
             
             // Because of the SF1 structure, the data will always fall into this exact order:
             const rawName = validDataCells[0] ? String(validDataCells[0]) : "";
             const rawGender = validDataCells[1] ? String(validDataCells[1]) : "N/A";
             const rawBirthday = validDataCells[2] ? String(validDataCells[2]) : "";
             const rawAge = validDataCells[3] ? String(validDataCells[3]) : "";

             if (rawName && rawName.trim() !== "") {
                let lastName = rawName.trim();
                let firstName = "";
                
                // Split Last and First name purely for clean sorting later
                if (rawName.includes(',')) {
                  const parts = rawName.split(',');
                  lastName = parts[0].trim();
                  firstName = parts[1] ? parts[1].trim() : "";
                }

                // Standardize Gender format (M/F to Male/Female)
                let genderFormat = "N/A";
                if (rawGender.toUpperCase().startsWith("M")) genderFormat = "Male";
                if (rawGender.toUpperCase().startsWith("F")) genderFormat = "Female";

                // Package the exact target data
                const studentPayload = {
                    lrn: lrn,
                    schoolYear: setupData.schoolYear, 
                    grade: excelGrade.toUpperCase(), 
                    section: excelSection.toUpperCase(), 
                    name: rawName.trim().toUpperCase(),
                    lastName: lastName.toUpperCase(), 
                    firstName: firstName.toUpperCase(), 
                    middleName: "", 
                    gender: genderFormat,
                    birthday: rawBirthday,
                    computedAge: rawAge,
                    updatedAt: serverTimestamp() 
                };

                if (existingMap[lrn]) {
                  // Update existing student safely without making clones
                  batch.update(existingMap[lrn], studentPayload);
                  updatedCount++;
                } else {
                  // Create new student
                  const newDocRef = doc(studentsRef);
                  studentPayload.createdAt = serverTimestamp();
                  batch.set(newDocRef, studentPayload);
                  addedCount++;
                }
             }
          }
        });

        await batch.commit();
        
        alert(`✅ Import Successful!\n\nAdded: ${addedCount} Students\nUpdated: ${updatedCount} Students`);
        await fetchAndSortStudents(setupData.schoolYear);

        setExcelGrade(''); setExcelSection('');

      } catch (err) { 
        alert("❌ Error reading the Excel file. Please ensure the file is not corrupted."); 
        console.error(err);
      } finally { 
        setLoading(false); 
        if (fileInputRef.current) fileInputRef.current.value = ""; 
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  const getQuarterGradeStr = (dataObj) => {
    if (!dataObj || dataObj.qaHps === 0) return null;
    const wwNum = dataObj.wwHps > 0 ? (dataObj.wwScore / dataObj.wwHps) * 40 : 0;
    const ptNum = dataObj.ptHps > 0 ? (dataObj.ptScore / dataObj.ptHps) * 40 : 0;
    const qaNum = (dataObj.qaScore / dataObj.qaHps) * 20;
    return (wwNum + ptNum + qaNum).toFixed(2);
  };

  const handlePrintReport = () => {
    const originalTitle = document.title;
    let timeLabel = reportQuarter;
    if (reportType === 'sf2') {
       const m = monthOptions.find(mo => mo.val === reportMonth);
       timeLabel = m ? m.label.toUpperCase() : reportMonth;
    } else if (reportQuarter === 'FINAL RATING') {
       timeLabel = 'FINAL_RATING';
    }

    let fileName = `${setupData.schoolYear}_${timeLabel}_${reportSubject}`;
    
    if (reportType === 'student' && reportStudent) {
       const safeStudentName = reportStudent.replace(/\s+/g, '_');
       fileName = `${setupData.schoolYear}_${timeLabel}_${safeStudentName}`;
    }
    
    document.title = fileName;
    window.print();
    setTimeout(() => { document.title = originalTitle; }, 500); 
  };

  const saveDailyAttendance = async () => {
    if (!selectedSubject) return alert("Please select a subject.");
    setLoading(true);
    try {
      const cleanList = attendanceList.filter(r => !r.isHeaderRow);
      await addDoc(collection(db, "users", user.uid, "attendance"), {
        schoolYear: setupData.schoolYear, date: attendanceDate, quarter: selectedQuarter, subject: selectedSubject, records: cleanList, timestamp: serverTimestamp()
      });
      alert("✅ Daily Attendance Saved!");
      goHome();
    } catch (err) { alert("Error saving attendance."); } 
    finally { setLoading(false); }
  };

  const searchStudentAttendance = async () => {
    if (!searchAttStudent || !searchAttSubject) return alert("Select Student and Subject");
    setLoading(true);
    try {
      const attRef = collection(db, "users", user.uid, "attendance");
      const q = query(attRef, where("schoolYear", "==", setupData.schoolYear), where("quarter", "==", selectedQuarter), where("subject", "==", searchAttSubject));
      const snap = await getDocs(q);
      let p = 0, l = 0, a = 0; let lDates = [], aDates = [];
      snap.forEach(doc => {
        const data = doc.data();
        const record = data.records.find(r => r.student === searchAttStudent);
        if (record) {
          if (record.status === 'Present') p++;
          if (record.status === 'Late') { l++; lDates.push(data.date); }
          if (record.status === 'Absent') { a++; aDates.push(data.date); }
        }
      });
      lDates.sort(); aDates.sort();
      setAttStats({ present: p, late: l, absent: a, lateDates: lDates, absentDates: aDates });
    } catch (err) { alert("Search error."); } 
    finally { setLoading(false); }
  };

  const getStudentAttendanceStats = (studentName) => {
    let p = 0, l = 0, a = 0;
    dashboardAttendance.forEach(doc => {
      const rec = doc.records.find(r => r.student === studentName);
      if (rec) {
        if (rec.status === 'Present') p++;
        if (rec.status === 'Late') l++;
        if (rec.status === 'Absent') a++;
      }
    });
    return { p, l, a };
  };

  const saveRapidScore = async () => {
    if (!rapidScore || !selectedStudent) return;
    if (parseFloat(rapidScore) > parseFloat(rapidHps)) return setSaveMessage(`⚠️ Score cannot exceed HPS (${rapidHps})!`);
    setLoading(true);
    try {
      const gradesRef = collection(db, "users", user.uid, "grades");
      const actualTaskIndex = selectedCategory === 'QA' ? 0 : rapidTaskIndex;
      const duplicateQuery = query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("student", "==", selectedStudent), where("quarter", "==", selectedQuarter), where("subject", "==", selectedSubject), where("category", "==", selectedCategory), where("taskIndex", "==", actualTaskIndex));
      const duplicateSnap = await getDocs(duplicateQuery);
      
      if (!duplicateSnap.empty) { setSaveMessage(`⚠️ Task already recorded for ${selectedStudent}! Edit in View Scores.`); setLoading(false); return; }

      await addDoc(gradesRef, {
        schoolYear: setupData.schoolYear, student: selectedStudent, quarter: selectedQuarter, subject: selectedSubject, category: selectedCategory, taskIndex: actualTaskIndex, score: rapidScore, hps: rapidHps, timestamp: serverTimestamp()
      });
      setSaveMessage(`✅ Saved score for ${selectedStudent}`); setRapidScore('');
      
      const list = getStudentListForSubject(selectedSubject);
      let nextIndex = list.indexOf(selectedStudent) + 1;
      while (nextIndex < list.length && isHeader(list[nextIndex])) { nextIndex++; }
      if (nextIndex < list.length) setSelectedStudent(list[nextIndex]);
      else { setSelectedStudent(''); setSaveMessage("🏁 Task completed for all students!"); }
    } catch (err) { setSaveMessage("❌ Error saving score."); } 
    finally { setLoading(false); }
  };

  const loadRecordsForStudent = async (studentToLoad, subjectToLoad) => {
    if (!selectedQuarter || !subjectToLoad || !studentToLoad) return;
    setLoading(true);
    try {
      if (activeTab !== 'ATTENDANCE') {
        const gradesRef = collection(db, "users", user.uid, "grades");
        const qGrades = query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("student", "==", studentToLoad), where("quarter", "==", selectedQuarter), where("subject", "==", subjectToLoad));
        const snapGrades = await getDocs(qGrades);
        const records = snapGrades.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        records.sort((a, b) => {
          const catOrder = { 'WW': 1, 'PT': 2, 'QA': 3 };
          if (a.category !== b.category) return catOrder[a.category] - catOrder[b.category]; 
          return a.taskIndex - b.taskIndex;
        });
        setEditRecords(records); setEditAttendanceStats(null); 
        if (records.length === 0) setSaveMessage("No score records found."); else setSaveMessage("");
      } else {
        const attRef = collection(db, "users", user.uid, "attendance");
        const qAtt = query(attRef, where("schoolYear", "==", setupData.schoolYear), where("quarter", "==", selectedQuarter), where("subject", "==", subjectToLoad));
        const snapAtt = await getDocs(qAtt);
        let p = 0, l = 0, a = 0; let lDates = [], aDates = [];
        snapAtt.forEach(doc => {
          const data = doc.data();
          const rec = data.records.find(r => r.student === studentToLoad);
          if (rec) {
            if (rec.status === 'Present') p++;
            if (rec.status === 'Late') { l++; lDates.push(data.date); }
            if (rec.status === 'Absent') { a++; aDates.push(data.date); }
          }
        });
        lDates.sort(); aDates.sort();
        setEditAttendanceStats({ present: p, late: l, absent: a, lateDates: lDates, absentDates: aDates });
        setEditRecords([]); 
        if (p === 0 && l === 0 && a === 0) setSaveMessage("No attendance records found."); else setSaveMessage("");
      }
    } catch (err) { alert("Error fetching records."); } 
    finally { setLoading(false); }
  };

  const handleUpdateSingleScore = async (docId, newScore, hps) => {
    if (parseFloat(newScore) > parseFloat(hps)) return alert(`Score cannot exceed HPS (${hps})`);
    try {
      await updateDoc(doc(db, "users", user.uid, "grades", docId), { score: newScore });
      setSaveMessage("✅ Score updated!"); setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) { alert("Error updating score."); }
  };

  const handleDeleteSingleScore = async (docId) => {
    if (!window.confirm("Delete this score completely?")) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "grades", docId));
      setEditRecords(prev => prev.filter(r => r.id !== docId));
      setSaveMessage("🗑️ Score deleted."); setTimeout(() => setSaveMessage(""), 3000);
    } catch (err) { alert("Error deleting score."); }
  };

  const buildGridData = (category) => {
    const grid = {};
    const currentList = getStudentListForSubject(dashboardSubject);
    currentList.forEach(s => { if (!isHeader(s)) grid[s] = { scores: Array(10).fill(''), hps: Array(10).fill(''), totalScore: 0, totalHps: 0 }; });
    dashboardGrades.forEach(g => {
      if (g.category === category && grid[g.student]) {
        grid[g.student].scores[g.taskIndex] = g.score; grid[g.student].hps[g.taskIndex] = g.hps;
        grid[g.student].totalScore += parseFloat(g.score || 0); grid[g.student].totalHps += parseFloat(g.hps || 0);
      }
    });
    return grid;
  };

  const generateReport = async () => {
    setLoading(true); setReportData(null); setStudentReportData(null);
    
    try {
      if (reportType === 'sf2') {
        if (!reportMonth || !reportSubject) { setLoading(false); return alert("Select Month and Subject"); }
        const attRef = collection(db, "users", user.uid, "attendance");
        const q = query(attRef, where("schoolYear", "==", setupData.schoolYear), where("subject", "==", reportSubject));
        const snap = await getDocs(q);

        const monthlyRecords = snap.docs
           .map(d => d.data())
           .filter(d => d.date && d.date.split('-')[1] === reportMonth);

        const uniqueDates = [...new Set(monthlyRecords.map(d => d.date))].sort();

        const summary = {};
        const currentList = getStudentListForSubject(reportSubject);
        
        currentList.forEach(st => {
          if (!isHeader(st)) {
             summary[st] = { present: 0, absent: 0, tardy: 0, daily: {} };
             uniqueDates.forEach(date => summary[st].daily[date] = ''); 
          }
        });

        monthlyRecords.forEach(dayRecord => {
           const d = dayRecord.date;
           dayRecord.records.forEach(r => {
              if (summary[r.student]) {
                 if (r.status === 'Present') {
                    summary[r.student].present++;
                    summary[r.student].daily[d] = '✔';
                 } else if (r.status === 'Absent') {
                    summary[r.student].absent++;
                    summary[r.student].daily[d] = 'A';
                 } else if (r.status === 'Late') {
                    summary[r.student].tardy++;
                    summary[r.student].daily[d] = 'L';
                 }
              }
           });
        });

        setReportData({ type: 'sf2', uniqueDates, summary });

      } else if (reportType === 'class') {
        if (!reportQuarter || !reportSubject) { setLoading(false); return alert("Select Quarter and Subject"); }
        const gradesRef = collection(db, "users", user.uid, "grades");
        let q = (reportQuarter === 'FINAL RATING') 
           ? query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("subject", "==", reportSubject))
           : query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("quarter", "==", reportQuarter), where("subject", "==", reportSubject));

        const snap = await getDocs(q);
        const grades = snap.docs.map(doc => doc.data());

        const summary = {};
        const currentList = getStudentListForSubject(reportSubject);
        currentList.forEach(st => {
          if (!isHeader(st)) {
            if (reportQuarter === 'FINAL RATING') {
               summary[st] = { Q1: { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 }, Q2: { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 }, Q3: { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 } };
            } else {
               summary[st] = { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 };
            }
          }
        });

        grades.forEach(g => {
          if (summary[g.student]) {
            const target = reportQuarter === 'FINAL RATING' ? summary[g.student][g.quarter] : summary[g.student];
            if (target) {
              if (g.category === 'WW') { target.wwScore += parseFloat(g.score || 0); target.wwHps += parseFloat(g.hps || 0); }
              if (g.category === 'PT') { target.ptScore += parseFloat(g.score || 0); target.ptHps += parseFloat(g.hps || 0); }
              if (g.category === 'QA') { target.qaScore += parseFloat(g.score || 0); target.qaHps += parseFloat(g.hps || 0); }
            }
          }
        });
        setReportData(summary);
        
      } else if (reportType === 'student') {
        if (!reportQuarter || !reportStudent || !reportSubject) { setLoading(false); return alert("Select Quarter, Subject, and Student"); }
        const gradesRef = collection(db, "users", user.uid, "grades");
        let q = (reportQuarter === 'FINAL RATING')
           ? query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("student", "==", reportStudent))
           : query(gradesRef, where("schoolYear", "==", setupData.schoolYear), where("quarter", "==", reportQuarter), where("student", "==", reportStudent));

        const snap = await getDocs(q);
        const grades = snap.docs.map(doc => doc.data());

        const summary = {};
        subjects.forEach(sub => {
           if (reportQuarter === 'FINAL RATING') {
              summary[sub] = { Q1: { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 }, Q2: { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 }, Q3: { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 } };
           } else {
              summary[sub] = { wwScore: 0, wwHps: 0, ptScore: 0, ptHps: 0, qaScore: 0, qaHps: 0 };
           }
        });

        grades.forEach(g => {
          if (summary[g.subject]) {
            const target = reportQuarter === 'FINAL RATING' ? summary[g.subject][g.quarter] : summary[g.subject];
            if (target) {
              if (g.category === 'WW') { target.wwScore += parseFloat(g.score || 0); target.wwHps += parseFloat(g.hps || 0); }
              if (g.category === 'PT') { target.ptScore += parseFloat(g.score || 0); target.ptHps += parseFloat(g.hps || 0); }
              if (g.category === 'QA') { target.qaScore += parseFloat(g.score || 0); target.qaHps += parseFloat(g.hps || 0); }
            }
          }
        });
        setStudentReportData(summary);
      }
    } catch (err) { alert("Error generating report."); } 
    finally { setLoading(false); }
  };

  const renderGradeComputation = () => {
    let wwScore = 0, wwHps = 0; let ptScore = 0, ptHps = 0; let qaScore = 0, qaHps = 0;
    editRecords.forEach(r => {
      if (r.category === 'WW') { wwScore += parseFloat(r.score || 0); wwHps += parseFloat(r.hps || 0); }
      if (r.category === 'PT') { ptScore += parseFloat(r.score || 0); ptHps += parseFloat(r.hps || 0); }
      if (r.category === 'QA') { qaScore += parseFloat(r.score || 0); qaHps += parseFloat(r.hps || 0); }
    });
    const wwPct = wwHps > 0 ? ((wwScore / wwHps) * 40).toFixed(2) : '0.00';
    const ptPct = ptHps > 0 ? ((ptScore / ptHps) * 40).toFixed(2) : '0.00';
    const qaPct = qaHps > 0 ? ((qaScore / qaHps) * 20).toFixed(2) : '0.00';
    const hasQA = qaHps > 0;
    const finalGrade = hasQA ? (parseFloat(wwPct) + parseFloat(ptPct) + parseFloat(qaPct)).toFixed(2) : '-';

    return (
      <div style={{ marginTop: '20px', backgroundColor: '#e8f4f8', padding: '15px', borderRadius: '8px', border: '1px solid #b3d4e0' }}>
        <h4 style={{ margin: '0 0 15px 0', textAlign: 'center', color: '#0047a0' }}>Grade Computation</h4>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', borderBottom: '1px solid #ccc', paddingBottom: '8px', marginBottom: '8px' }}>
          <span style={{ fontWeight: 'bold', color: '#333' }}>Written Works (40%)</span><span>{wwScore} / {wwHps} = <strong style={{ color: '#0047a0' }}>{wwPct}</strong></span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', borderBottom: '1px solid #ccc', paddingBottom: '8px', marginBottom: '8px' }}>
          <span style={{ fontWeight: 'bold', color: '#333' }}>Performance Tasks (40%)</span><span>{ptScore} / {ptHps} = <strong style={{ color: '#0047a0' }}>{ptPct}</strong></span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', borderBottom: '1px solid #ccc', paddingBottom: '8px', marginBottom: '15px' }}>
          <span style={{ fontWeight: 'bold', color: '#333' }}>Quarterly Assessment (20%)</span><span>{qaScore} / {qaHps} = <strong style={{ color: '#0047a0' }}>{qaPct}</strong></span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '16px', fontWeight: 'bold', color: hasQA ? '#27ae60' : '#888', backgroundColor: '#fff', padding: '12px', borderRadius: '5px', border: hasQA ? '2px solid #2ecc71' : '2px dashed #ccc' }}>
          <span>QUARTER GRADE</span><span style={{ fontSize: '20px' }}>{finalGrade}</span>
        </div>
        {!hasQA && <div style={{ fontSize: '11px', textAlign: 'center', color: '#e67e22', marginTop: '8px', fontWeight: 'bold' }}>*Quarter grade will be computed once QA is recorded.</div>}
      </div>
    );
  };

  const availableGrades = [...new Set(allRawStudents.map(s => s.grade).filter(Boolean))].sort();
  const availableSections = manualGrade 
    ? [...new Set(allRawStudents.filter(s => s.grade === manualGrade).map(s => s.section).filter(Boolean))].sort()
    : [];

  const btnStyle = (color) => ({ width: '100%', padding: '12px', fontSize: '16px', fontWeight: 'bold', backgroundColor: color, color: 'white', border: 'none', borderRadius: '8px', marginBottom: '10px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' });
  const inputStyle = { width: '100%', padding: '10px', fontSize: '15px', borderRadius: '5px', border: '1px solid #aaa', marginBottom: '12px', boxSizing: 'border-box', color: '#000', backgroundColor: '#fdfdfd', fontWeight: 'bold' };
  const labelStyle = { fontWeight: 'bold', display: 'block', marginBottom: '4px', color: '#333', fontSize: '12px', textTransform: 'uppercase' };
  const tabStyle = (isActive) => ({ padding: '8px 6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', border: '1px solid #aaa', borderBottom: isActive ? 'none' : '1px solid #aaa', backgroundColor: isActive ? '#fff' : '#eaeaea', color: isActive ? '#0047a0' : '#555', borderTopLeftRadius: '5px', borderTopRightRadius: '5px', flex: 1, textAlign: 'center', position: 'relative', top: isActive ? '1px' : '0', zIndex: isActive ? 10 : 1 });

  return (
    <>
      <style>{`
        @page { size: A4 landscape; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body, html, #root { 
            background: white !important; 
            margin: 0 !important; 
            padding: 0 !important; 
            width: 100% !important; 
            max-width: 100% !important; 
            overflow: visible !important; 
            display: block !important;
          }
          .print-container { 
            width: 100% !important; 
            max-width: 297mm !important; 
            display: block !important; 
            overflow: visible !important; 
            box-shadow: none !important;
          }
          .print-container * { 
            overflow: visible !important; 
          }
          table { 
            width: 100% !important; 
            border-collapse: collapse !important; 
            table-layout: auto !important; 
            page-break-inside: auto !important;
          }
          tr { 
            page-break-inside: avoid !important; 
            page-break-after: auto !important;
          }
          th, td { 
            border: 1px solid #000 !important; 
            padding: 4px !important; 
            font-size: 11px !important; 
            text-align: center !important; 
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          td:first-child, th:first-child {
            text-align: left !important;
            padding-left: 8px !important;
          }
          .text-left { text-align: left !important; }
        }
      `}</style>

      <div className="print-container" style={{ padding: 0, fontFamily: 'Arial, sans-serif', width: '100%', maxWidth: '450px', margin: '0 auto', backgroundColor: '#e0e0e0', height: '100%', flex: 1, display: 'flex', flexDirection: 'column', color: '#000', boxSizing: 'border-box', overflow: 'hidden' }}>
        
        {/* --- TEACHER PROFILE MODAL --- */}
        {showProfileModal && (
          <div className="no-print" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', width: '80%', maxWidth: '350px' }}>
              <h3 style={{ margin: '0 0 15px 0', borderBottom: '2px solid #0047a0', paddingBottom: '5px', color: '#0047a0' }}>Teacher Profile</h3>
              <p style={{margin: '8px 0'}}><strong>Name:</strong> {setupData.teacherName || 'Not Set'}</p>
              <p style={{margin: '8px 0'}}><strong>School:</strong> {setupData.schoolName || 'Not Set'}</p>
              <p style={{margin: '8px 0'}}><strong>Address:</strong> {setupData.address || 'Not Set'}</p>
              <button onClick={() => setShowProfileModal(false)} style={{ width: '100%', padding: '10px', backgroundColor: '#333', color: '#fff', border: 'none', borderRadius: '5px', marginTop: '15px', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
            </div>
          </div>
        )}

        {/* --- VIEW: MAIN DASHBOARD --- */}
        {view === 'menu' && (
          <div className="no-print" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            
            {/* TITLE BAR */}
            <div style={{ backgroundColor: '#0047a0', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', flexShrink: 0 }}>
              <div style={{ flex: 1, paddingRight: '15px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', fontFamily: '"Arial Narrow", Arial, sans-serif', letterSpacing: '-0.5px', color: '#f39c12', whiteSpace: 'nowrap', display: 'inline-block', transform: 'scaleX(0.9)', transformOrigin: 'left' }}>GNEWS CLASS RECORD</h2>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '4px', lineHeight: '1.3' }}>{setupData.schoolName ? setupData.schoolName.toUpperCase() : "SCHOOL NAME"}</div>
              </div>
              <div onClick={() => setShowProfileModal(true)} style={{ width: '45px', height: '45px', borderRadius: '50%', border: '2px solid #000', backgroundColor: '#003380', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold', flexShrink: 0, marginRight: '5px' }}>
                {setupData.teacherName ? setupData.teacherName.charAt(0).toUpperCase() : "T"}
              </div>
            </div>

            {/* HEADER INFO */}
            <div style={{ padding: '15px 20px 5px 20px', flexShrink: 0 }}>
              <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333', display: 'flex', alignItems: 'center', gap: '5px', textTransform: 'uppercase' }}>
                {setupData.schoolYear ? setupData.schoolYear : "SCHOOL YEAR"}, 
                <select style={{ border: 'none', backgroundColor: 'transparent', fontWeight: 'bold', fontSize: '14px', outline: 'none', cursor: 'pointer', color: '#0047a0', padding: 0 }} value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)}>
                  <option value="Q1">QUARTER 1</option><option value="Q2">QUARTER 2</option><option value="Q3">QUARTER 3</option>
                </select>
              </div>
              <h1 style={{ fontWeight: '300', margin: '10px 0 5px 0', fontSize: '28px', color: '#222' }}>HELLO, <span style={{ fontWeight: 'bold' }}>{setupData.teacherName ? setupData.teacherName.split(' ')[0] : 'Teacher'}</span></h1>
              <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#555', letterSpacing: '1px', marginBottom: '15px' }}>{getCurrentDateStr()}</div>
            </div>

            {/* --- PREMIUM INSTALL BUTTON (GATEKEEPER) --- */}
            {isPWA && deferredPrompt && (
              <div style={{ backgroundColor: '#fff3cd', padding: '15px', margin: '0 20px 15px 20px', borderRadius: '8px', border: '2px solid #ffc107', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>🌟 Premium PWA Upgrade Active!</h4>
                <p style={{ fontSize: '12px', color: '#664d03', marginBottom: '15px' }}>
                  Install GNEWS Class Record directly to your device for a faster, full-screen, native app experience without using the web browser.
                </p>
                <button 
                  onClick={handleInstallClick} 
                  style={{ width: '100%', padding: '12px', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}
                >
                  📱 Install App to Device
                </button>
              </div>
            )}

            {/* BIG ACTION BUTTONS */}
            <div style={{ display: 'flex', gap: '15px', padding: '10px 20px', flexShrink: 0 }}>
              <div onClick={() => setView('attendance_menu')} style={{ flex: 1, backgroundColor: '#003380', color: '#fff', padding: '20px 10px', textAlign: 'center', borderRadius: '5px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: '1.3' }}>CHECK<br/>ATTENDANCE</span>
              </div>
              <div onClick={() => setView('rapid_input')} style={{ flex: 1, backgroundColor: '#003380', color: '#fff', padding: '20px 10px', textAlign: 'center', borderRadius: '5px', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: '1.3' }}>RECORD<br/>SCORES</span>
              </div>
            </div>

            {/* SCROLLABLE TABS TABLE */}
            <div style={{ padding: '10px 20px 0 20px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                <span style={{fontSize: '12px', fontWeight: 'bold', color: '#555'}}>Showing:</span>
                <select style={{ padding: '4px', fontSize: '12px', fontWeight: 'bold', borderRadius: '4px', border: '1px solid #ccc', maxWidth: '200px' }} value={dashboardSubject} onChange={e => setDashboardSubject(e.target.value)}>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div style={{ display: 'flex', borderBottom: '1px solid #aaa', flexShrink: 0 }}>
                {['ATTENDANCE', 'WW RECORD', 'PT RECORD', 'QA RECORD'].map(tab => (
                  <div key={tab} onClick={() => setActiveTab(tab)} style={tabStyle(activeTab === tab)}>{tab.split(' ')[0]}</div>
                ))}
              </div>
              
              <div style={{ backgroundColor: '#fff', border: '1px solid #aaa', borderTop: 'none', flex: 1, overflowY: 'auto', borderBottomLeftRadius: '5px', borderBottomRightRadius: '5px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#eaeaea', zIndex: 5, borderBottom: '2px solid #aaa' }}>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'left', borderRight: '1px solid #ccc', width: '110px', maxWidth: '110px', whiteSpace: 'nowrap', overflow: 'hidden' }}>STUDENT NAME</th>
                      {activeTab === 'ATTENDANCE' && <><th style={{padding:'8px', textAlign:'center'}}>P</th><th style={{padding:'8px', textAlign:'center'}}>L</th><th style={{padding:'8px', textAlign:'center'}}>A</th></>}
                      {(activeTab === 'WW RECORD' || activeTab === 'PT RECORD') && <th style={{padding:'8px', textAlign:'center'}}>TOTAL</th>}
                      {activeTab === 'QA RECORD' && <th style={{padding:'8px', textAlign:'center'}}>EXAM SCORE</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {getStudentListForSubject(dashboardSubject).map((st, idx) => {
                      const isClassHeader = st.startsWith('GRADE ') || st === 'ALL STUDENTS' || st === 'UNASSIGNED CLASS';
                      const isGenderHeader = st === 'MALE' || st === 'FEMALE';

                      if (isClassHeader) return <tr key={idx}><td colSpan="4" style={{ padding: '6px 8px', backgroundColor: '#2c3e50', color: '#fff', fontWeight: 'bold', fontSize: '12px', textAlign: 'center', textTransform: 'uppercase' }}>{st}</td></tr>;
                      if (isGenderHeader) return <tr key={idx}><td colSpan="4" style={{ padding: '4px 8px', backgroundColor: '#f5f5f5', color: '#0047a0', fontWeight: 'bold', fontSize: '11px' }}>{st}</td></tr>;
                      
                      if (activeTab === 'ATTENDANCE') {
                        const att = getStudentAttendanceStats(st);
                        return (
                          <tr key={idx} onDoubleClick={() => handleStudentDoubleClick(st)} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}>
                            <td style={{ padding: '8px', borderRight: '1px solid #eee', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '110px', maxWidth: '110px' }} title={st}>{st}</td>
                            <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', color: att.p > 0 ? '#27ae60' : '#aaa' }}>{att.p || '-'}</td>
                            <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', color: att.l > 0 ? '#f1c40f' : '#aaa' }}>{att.l || '-'}</td>
                            <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', color: att.a > 0 ? '#e74c3c' : '#aaa' }}>{att.a || '-'}</td>
                          </tr>
                        );
                      }

                      const cat = activeTab === 'WW RECORD' ? 'WW' : (activeTab === 'PT RECORD' ? 'PT' : 'QA');
                      const grid = buildGridData(cat);
                      const rowData = grid[st];

                      return (
                        <tr key={idx} onDoubleClick={() => handleStudentDoubleClick(st)} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}>
                          <td style={{ padding: '8px', borderRight: '1px solid #eee', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '110px', maxWidth: '110px' }} title={st}>{st}</td>
                          {(activeTab === 'WW RECORD' || activeTab === 'PT RECORD') && rowData && <td style={{padding:'8px', textAlign:'center', fontWeight:'bold', color: '#0047a0', backgroundColor:'#f9f9f9'}}>{rowData.totalScore || '-'}</td>}
                          {activeTab === 'QA RECORD' && rowData && <td style={{padding:'8px', textAlign:'center', fontWeight:'bold', color: '#0047a0'}}>{rowData.scores[0] || '-'}</td>}
                        </tr>
                      )
                    })}
                    {getStudentListForSubject(dashboardSubject).length === 0 && <tr><td colSpan="4" style={{padding: '20px', textAlign: 'center', color: '#888'}}>No students assigned to this class.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{fontSize: '10px', textAlign: 'center', padding: '5px', color: '#888', fontStyle: 'italic', flexShrink: 0}}>💡 Double-click a student name to securely view/edit records.</div>
            </div>
          </div>
        )}

        {view !== 'menu' && (
          <div className="no-print" style={{ backgroundColor: '#0047a0', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', flexShrink: 0 }}>
            <h3 style={{ margin: '0', fontSize: '16px', fontWeight: 'bold', textTransform: 'uppercase' }}>
              {view === 'rapid_input' ? 'RECORD SCORES' : view === 'edit_scores' ? (activeTab === 'ATTENDANCE' ? 'VIEW ATTENDANCE' : 'VIEW SCORES') : view === 'setup' ? 'SYSTEM SETUP' : view === 'register' ? 'STUDENT REGISTRY' : view === 'subjects' ? 'MY SUBJECTS' : view === 'attendance_menu' ? 'ATTENDANCE HUB' : view === 'attendance_daily' ? 'DAILY ATTENDANCE' : view === 'attendance_search' ? 'VIEW ATTENDANCE' : view === 'reports' ? 'REPORTS CENTER' : view.replace('_', ' ')}
            </h3>
          </div>
        )}

        {/* Sub-views wrapper for scrolling */}
        {view !== 'menu' && (
           <div className={view === 'reports' ? '' : 'no-print'} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', paddingBottom: '20px' }}>
             
             {view === 'attendance_menu' && (
               <div style={{ padding: '0 15px' }}>
                 <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', border: '1px solid #ccc', textAlign: 'center' }}>
                   <h4 style={{ margin: '0 0 15px 0', color: '#0047a0' }}>Choose an Action</h4>
                   <button onClick={() => setView('attendance_daily')} style={btnStyle('#27ae60')}>📝 Record Daily Attendance</button>
                   <button onClick={() => setView('attendance_search')} style={btnStyle('#2980b9')}>🔍 View Attendance</button>
                 </div>
               </div>
             )}

             {view === 'attendance_daily' && (
               <div style={{ padding: '0 15px' }}>
                 <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #ccc' }}>
                   <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                     <input type="date" style={{...inputStyle, flex: 1, minWidth: 0, marginBottom: 0}} value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} />
                     <select style={{...inputStyle, flex: 1, minWidth: 0, marginBottom: 0}} value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)}>
                       {['Q1','Q2','Q3'].map(q => <option key={q} value={q}>{q}</option>)}
                     </select>
                   </div>
                   <select style={{...inputStyle, marginBottom: '15px'}} value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                     <option value="">-- Select Subject --</option>
                     {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                   </select>

                   <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', paddingBottom: '8px', borderBottom: '2px solid #000', marginBottom: '8px', fontSize: '11px', fontWeight: 'bold', color: '#000' }}>
                     <span>P</span><span>L</span><span>A</span>
                   </div>

                   {attendanceList.map((record, index) => {
                     if (record.isHeaderRow) {
                       return <div key={index} style={{ padding: '8px 0', backgroundColor: record.student.startsWith('GRADE') || record.student === 'ALL STUDENTS' ? '#0047a0' : '#f5f5f5', color: record.student.startsWith('GRADE') || record.student === 'ALL STUDENTS' ? '#fff' : '#0047a0', fontWeight: 'bold', fontSize: record.student.startsWith('GRADE') || record.student === 'ALL STUDENTS' ? '12px' : '11px', textAlign: 'center', marginTop: '10px', borderRadius: '4px' }}>{record.student}</div>;
                     }
                     return (
                       <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #ddd' }}>
                         <span style={{ fontWeight: 'bold', fontSize: '14px', flex: 1, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>{record.student}</span>
                         <div style={{ display: 'flex', gap: '6px' }}>
                           <button onClick={() => { const newList = [...attendanceList]; newList[index].status = 'Present'; setAttendanceList(newList); }} style={{ width: '35px', height: '35px', border: 'none', borderRadius: '5px', backgroundColor: record.status === 'Present' ? '#2ecc71' : '#eee', color: record.status === 'Present' ? 'white' : '#555', fontWeight: 'bold', cursor: 'pointer' }}>P</button>
                           <button onClick={() => { const newList = [...attendanceList]; newList[index].status = 'Late'; setAttendanceList(newList); }} style={{ width: '35px', height: '35px', border: 'none', borderRadius: '5px', backgroundColor: record.status === 'Late' ? '#f1c40f' : '#eee', color: record.status === 'Late' ? 'white' : '#555', fontWeight: 'bold', cursor: 'pointer' }}>L</button>
                           <button onClick={() => { const newList = [...attendanceList]; newList[index].status = 'Absent'; setAttendanceList(newList); }} style={{ width: '35px', height: '35px', border: 'none', borderRadius: '5px', backgroundColor: record.status === 'Absent' ? '#e74c3c' : '#eee', color: record.status === 'Absent' ? 'white' : '#555', fontWeight: 'bold', cursor: 'pointer' }}>A</button>
                         </div>
                       </div>
                     );
                   })}
                   <button onClick={saveDailyAttendance} style={{...btnStyle('#0047a0'), marginTop: '30px', marginBottom: '20px'}}>{loading ? "Saving..." : "💾 Save Attendance"}</button>
                 </div>
               </div>
             )}

             {view === 'attendance_search' && (
               <div style={{ padding: '0 15px' }}>
                 <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #ccc' }}>
                   <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                     <select style={{...inputStyle, flex: 1, minWidth: 0, marginBottom: 0}} value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)}>{['Q1','Q2','Q3'].map(q => <option key={q} value={q}>{q}</option>)}</select>
                     <select style={{...inputStyle, flex: 2, minWidth: 0, marginBottom: 0}} value={searchAttSubject} onChange={e => {setSearchAttSubject(e.target.value); setSearchAttStudent('');}}>
                       <option value="">-- Subject --</option>{subjects.map(s => <option key={s} value={s}>{s}</option>)}
                     </select>
                   </div>
                   <select style={inputStyle} value={searchAttStudent} onChange={e => setSearchAttStudent(e.target.value)}>
                     <option value="">-- Select Student --</option>{getStudentListForSubject(searchAttSubject).filter(s => !isHeader(s)).map((st, i) => <option key={i} value={st}>{st}</option>)}
                   </select>
                   <button onClick={searchStudentAttendance} style={{...btnStyle('#2980b9'), marginTop: '10px'}}>{loading ? "Searching..." : "🔍 Load Attendance"}</button>

                   {attStats && (
                     <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '2px solid #2980b9' }}>
                       <h4 style={{ margin: '0 0 10px 0', textAlign: 'center', color: '#2980b9' }}>Attendance Summary</h4>
                       <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', backgroundColor: '#fff', borderRadius: '5px', marginBottom: '15px', border: '1px solid #ddd' }}>
                         <div style={{textAlign: 'center'}}><span style={{display: 'block', fontSize: '24px', fontWeight: '900', color: '#27ae60'}}>{attStats.present}</span><span style={{fontSize: '11px', fontWeight: 'bold'}}>Present</span></div>
                         <div style={{textAlign: 'center'}}><span style={{display: 'block', fontSize: '24px', fontWeight: '900', color: '#f1c40f'}}>{attStats.late}</span><span style={{fontSize: '11px', fontWeight: 'bold'}}>Late</span></div>
                         <div style={{textAlign: 'center'}}><span style={{display: 'block', fontSize: '24px', fontWeight: '900', color: '#e74c3c'}}>{attStats.absent}</span><span style={{fontSize: '11px', fontWeight: 'bold'}}>Absent</span></div>
                       </div>
                       {attStats.late > 0 && <div style={{ marginBottom: '10px', padding: '12px', backgroundColor: '#fef9e7', borderRadius: '5px', border: '1px solid #f1c40f' }}><h5 style={{ margin: '0 0 5px 0', color: '#d68910' }}>Dates Late</h5><ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>{attStats.lateDates.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
                       {attStats.absent > 0 && <div style={{ padding: '12px', backgroundColor: '#fdedec', borderRadius: '5px', border: '1px solid #e74c3c' }}><h5 style={{ margin: '0 0 5px 0', color: '#c0392b' }}>Dates Absent</h5><ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>{attStats.absentDates.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
                     </div>
                   )}
                 </div>
               </div>
             )}

             {view === 'rapid_input' && (
               <div style={{ padding: '0 15px' }}>
                 <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #ccc' }}>
                   <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                     <select style={{...inputStyle, marginBottom: 0, flex: 1.2, minWidth: 0}} value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)}>{['Q1','Q2','Q3'].map(q => <option key={q} value={q}>{q}</option>)}</select>
                     <select style={{...inputStyle, marginBottom: 0, flex: 2, minWidth: 0}} value={selectedSubject} onChange={e => {setSelectedSubject(e.target.value); setSelectedStudent('');}}><option value="">-- Subject --</option>{subjects.map((s, i) => <option key={i} value={s}>{s}</option>)}</select>
                   </div>
                   <div style={{ display: 'flex', gap: '6px' }}>
                     <select style={{...inputStyle, marginBottom: 0, flex: 1.2, minWidth: 0}} value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); if(e.target.value === 'QA') setRapidTaskIndex(0); }}><option value="WW">WW</option><option value="PT">PT</option><option value="QA">QA</option></select>
                     {selectedCategory !== 'QA' && <select style={{...inputStyle, marginBottom: 0, flex: 1.2, minWidth: 0}} value={rapidTaskIndex} onChange={e => setRapidTaskIndex(parseInt(e.target.value))}>{Array.from({length: 10}).map((_, i) => <option key={i} value={i}>Task {i + 1}</option>)}</select>}
                     <input type="number" placeholder="HPS" style={{...inputStyle, marginBottom: 0, flex: 1.2, minWidth: 0, backgroundColor: '#fff3cd'}} value={rapidHps} onChange={e => setRapidHps(e.target.value)} />
                   </div>
                   <hr style={{margin: '15px 0'}} />
                   <label style={labelStyle}>Student</label>
                   <select style={inputStyle} value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}>
                     <option value="">-- Select Student --</option>{getStudentListForSubject(selectedSubject).map((st, i) => <option key={i} value={st} disabled={isHeader(st)}>{isHeader(st) ? `▬▬ ${st} ▬▬` : st}</option>)}
                   </select>
                   <input type="number" style={{...inputStyle, fontSize: '24px', textAlign: 'center', border: '2px solid #0047a0', padding: '15px'}} placeholder="Score & Enter" value={rapidScore} onChange={e => setRapidScore(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveRapidScore()} />
                   <p style={{fontSize: '13px', textAlign: 'center', fontWeight: 'bold', color: saveMessage.includes('⚠️') ? '#e67e22' : '#27ae60'}}>{saveMessage}</p>
                 </div>
               </div>
             )}

             {view === 'edit_scores' && (
               <div style={{ padding: '0 15px' }}>
                 <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #ccc' }}>
                   <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                     <select style={{...inputStyle, marginBottom: 0, flex: 1.2, minWidth: 0}} value={selectedQuarter} onChange={e => {setSelectedQuarter(e.target.value); setEditRecords([]); setEditAttendanceStats(null);}}>{['Q1','Q2','Q3'].map(q => <option key={q} value={q}>{q}</option>)}</select>
                     <select style={{...inputStyle, marginBottom: 0, flex: 2, minWidth: 0}} value={selectedSubject} onChange={e => {setSelectedSubject(e.target.value); setSelectedStudent(''); setEditRecords([]); setEditAttendanceStats(null);}}><option value="">-- Subject --</option>{subjects.map((s, i) => <option key={i} value={s}>{s}</option>)}</select>
                   </div>
                   <select style={inputStyle} value={selectedStudent} onChange={e => {setSelectedStudent(e.target.value); setEditRecords([]); setEditAttendanceStats(null);}}><option value="">-- Select Student --</option>{getStudentListForSubject(selectedSubject).map((st, i) => ( <option key={i} value={st} disabled={isHeader(st)}>{isHeader(st) ? `▬▬ ${st} ▬▬` : st}</option> ))}</select>
                   
                   <button onClick={() => loadRecordsForStudent(selectedStudent, selectedSubject)} style={btnStyle('#0047a0')}>{loading ? "Loading..." : (activeTab === 'ATTENDANCE' ? "🔍 Load Attendance" : "🔍 Load Detailed Scores")}</button>
                   <p style={{fontSize: '13px', textAlign: 'center', fontWeight: 'bold', color: '#e74c3c'}}>{saveMessage}</p>

                   {activeTab === 'ATTENDANCE' ? (
                     editAttendanceStats && (
                       <div style={{ marginTop: '15px' }}>
                         <h4 style={{ margin: '0 0 10px 0', textAlign: 'center', color: '#0047a0' }}>{selectedStudent}'s Attendance View</h4>
                         <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px', border: '1px solid #ddd', display: 'flex', justifyContent: 'space-around' }}>
                           <div style={{textAlign: 'center'}}><span style={{display: 'block', fontSize: '24px', fontWeight: '900', color: '#27ae60'}}>{editAttendanceStats.present}</span><span style={{fontSize: '11px', fontWeight: 'bold', color: '#555'}}>Present</span></div>
                           <div style={{textAlign: 'center'}}><span style={{display: 'block', fontSize: '24px', fontWeight: '900', color: '#f1c40f'}}>{editAttendanceStats.late}</span><span style={{fontSize: '11px', fontWeight: 'bold', color: '#555'}}>Late</span></div>
                           <div style={{textAlign: 'center'}}><span style={{display: 'block', fontSize: '24px', fontWeight: '900', color: '#e74c3c'}}>{editAttendanceStats.absent}</span><span style={{fontSize: '11px', fontWeight: 'bold', color: '#555'}}>Absent</span></div>
                         </div>
                         {editAttendanceStats.late > 0 && <div style={{ marginBottom: '10px', padding: '12px', backgroundColor: '#fef9e7', borderRadius: '5px', border: '1px solid #f1c40f' }}><h5 style={{ margin: '0 0 5px 0', color: '#d68910' }}>Dates Late</h5><ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>{editAttendanceStats.lateDates.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
                         {editAttendanceStats.absent > 0 && <div style={{ padding: '12px', backgroundColor: '#fdedec', borderRadius: '5px', border: '1px solid #e74c3c' }}><h5 style={{ margin: '0 0 5px 0', color: '#c0392b' }}>Dates Absent</h5><ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>{editAttendanceStats.absentDates.map((d, i) => <li key={i}>{d}</li>)}</ul></div>}
                       </div>
                     )
                   ) : (
                     editRecords.length > 0 && (
                       <div style={{ marginTop: '15px', borderTop: '2px solid #eee', paddingTop: '15px' }}>
                         <h4 style={{ margin: '0 0 10px 0', textAlign: 'center', color: '#0047a0' }}>{selectedStudent}'s Score View</h4>
                         {editRecords.map((record, idx) => (
                           <div key={record.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px', border: '1px solid #ddd' }}>
                             <div style={{ flex: 1.5, fontWeight: 'bold', fontSize: '13px' }}>{record.category} {record.category !== 'QA' ? `Task ${record.taskIndex + 1}` : ''}<div style={{fontSize: '10px', color: '#888', marginTop: '2px'}}>HPS: {record.hps}</div></div>
                             <input type="number" style={{...inputStyle, flex: 1, minWidth: 0, marginBottom: 0, padding: '8px', textAlign: 'center'}} value={record.score} onChange={(e) => { const updated = [...editRecords]; updated[idx].score = e.target.value; setEditRecords(updated); }} />
                             <button onClick={() => handleUpdateSingleScore(record.id, record.score, record.hps)} style={{ flex: 1, padding: '8px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>Save</button>
                             <button onClick={() => handleDeleteSingleScore(record.id)} style={{ padding: '8px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>X</button>
                           </div>
                         ))}
                         {renderGradeComputation()}
                       </div>
                     )
                   )}
                 </div>
               </div>
             )}

             {view === 'subjects' && (
               <div style={{ padding: '0 15px' }}>
                 <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #ccc' }}>
                   
                   <h4 style={{ margin: '0 0 10px 0', color: '#0047a0' }}>My Official Class Load</h4>
                   <p style={{ fontSize: '11px', color: '#888', marginBottom: '15px', fontStyle: 'italic' }}>
                     Add classes manually below, or use the automated Excel Import.
                   </p>

                   {/* --- NEW: MANUAL ADD UI --- */}
                   <div style={{ backgroundColor: '#f0f4f8', padding: '15px', borderRadius: '5px', marginBottom: '20px', border: '1px solid #b3d4e0' }}>
                     <h5 style={{ margin: '0 0 10px 0', color: '#0047a0' }}>➕ Add New Subject Manually</h5>
                     <p style={{ fontSize: '10px', color: '#666', marginBottom: '10px' }}>
                       *Grades and Sections are automatically loaded from your imported Master Rosters.
                     </p>
                     
                     <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                        <select 
                           value={manualSubject} 
                           onChange={e => setManualSubject(e.target.value)}
                           style={{ flex: 1.5, minWidth: 0, padding: '8px', border: '1px solid #aaa', borderRadius: '4px', fontSize: '12px' }}
                        >
                           <option value="">-- Subject --</option>
                           {standardSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select 
                           value={manualGrade} 
                           onChange={e => {setManualGrade(e.target.value); setManualSection('');}} 
                           style={{ flex: 1, minWidth: 0, padding: '8px', border: '1px solid #aaa', borderRadius: '4px', fontSize: '12px' }} 
                        >
                           <option value="">Grade</option>
                           {availableGrades.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <select 
                           value={manualSection} 
                           onChange={e => setManualSection(e.target.value)} 
                           style={{ flex: 1.2, minWidth: 0, padding: '8px', border: '1px solid #aaa', borderRadius: '4px', fontSize: '12px', textTransform: 'uppercase' }} 
                           disabled={!manualGrade}
                        >
                           <option value="">Section</option>
                           {availableSections.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                        </select>
                     </div>
                     <button 
                        onClick={handleAddManualSubject} 
                        style={{ width: '100%', padding: '10px', backgroundColor: '#2980b9', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}
                     >
                        {loading ? "Saving..." : "Add to My Load"}
                     </button>
                   </div>

                   {subjects.length === 0 ? (
                     <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '5px', marginBottom: '20px' }}>
                       <p style={{ fontSize: '14px', color: '#888' }}>No subjects loaded. Please add them above.</p>
                     </div>
                   ) : (
                     <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '0 0 20px 0' }}>
                       {subjects.map((sub, i) => (
                         <li key={i} style={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           padding: '12px', 
                           borderBottom: '1px solid #eee', 
                           backgroundColor: '#fdfdfd', 
                           marginBottom: '5px', 
                           borderRadius: '5px',
                           borderLeft: '4px solid #0047a0' 
                         }}>
                           <strong style={{ fontSize: '14px', color: '#333' }}>{sub}</strong>
                         </li>
                       ))}
                     </ul>
                   )}

                   <div style={{ paddingTop: '15px', borderTop: '2px dashed #eee' }}>
                     <h4 style={{ margin: '0 0 10px 0', color: '#27ae60' }}>📥 Master Class Roster Import</h4>
                     <p style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>Type the Grade and Section, then upload the Excel file. The app will automatically build your registry.</p>
                     
                     <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                       <input type="text" placeholder="Grade (e.g. 10)" maxLength={2} value={excelGrade} onChange={e => setExcelGrade(e.target.value)} style={{ flex: 1, minWidth: 0, padding: '8px', border: '1px solid #aaa', borderRadius: '4px', fontSize: '12px', textTransform: 'uppercase', textAlign: 'center' }} />
                       <input type="text" placeholder="Section (e.g. RIZAL)" value={excelSection} onChange={e => setExcelSection(e.target.value)} style={{ flex: 2, minWidth: 0, padding: '8px', border: '1px solid #aaa', borderRadius: '4px', fontSize: '12px', textTransform: 'uppercase' }} />
                     </div>

                     <input type="file" accept=".xlsx, .xls, .csv" ref={fileInputRef} onChange={handleImportMasterRoster} style={{ fontSize: '12px', width: '100%', padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ccc', borderRadius: '5px' }} />
                     {loading && <p style={{fontSize: '12px', color: '#27ae60', fontWeight: 'bold', marginTop: '10px'}}>Processing Excel File...</p>}
                   </div>

                 </div>
               </div>
             )}

             {view === 'register' && (
               <div style={{ padding: '0 15px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                 <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #ccc', display: 'flex', flexDirection: 'column', flex: 1 }}>
                   <h4 style={{margin: '0 0 10px 0', color: '#0047a0', flexShrink: 0}}>Full Class List</h4>
                   <div style={{flex: 1, overflowY: 'auto', backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '5px', border: '1px solid #eee', minHeight: '200px'}}>
                     {allRawStudents.length === 0 ? <p style={{fontSize: '13px', color: '#888'}}>No students added yet. Please import a file from the Subjects tab.</p> : null}
                     {getGroupedRegistryList().map((s, i) => {
                       const isClassHeader = s.startsWith('GRADE ') || s === 'UNASSIGNED CLASS';
                       const isGenderHeader = s === 'MALE' || s === 'FEMALE';
                       let styling = { fontSize: '14px', padding: '8px 0', borderBottom: '1px solid #ddd', color: '#333', cursor: 'pointer' };
                       if (isClassHeader) styling = { fontSize: '14px', padding: '10px 0', borderBottom: '2px solid #e67e22', fontWeight: '900', color: '#fff', backgroundColor: '#e67e22', marginTop: i === 0 ? '0' : '20px', textAlign: 'center', borderRadius: '4px' };
                       else if (isGenderHeader) styling = { fontSize: '12px', padding: '6px 0', borderBottom: '2px solid #0047a0', fontWeight: 'bold', color: '#0047a0', marginTop: '10px' };
                       return <div key={i} onDoubleClick={() => handleStudentDoubleClick(s)} title={isClassHeader || isGenderHeader ? "" : "Double-click to view records"} style={styling}>{s}</div>;
                     })}
                   </div>
                   <p style={{fontSize: '10px', textAlign: 'center', marginTop: '5px', color: '#888', flexShrink: 0}}>Double-click a name to securely view their records.</p>
                 </div>
               </div>
             )}

             {view === 'setup' && <Settings setupData={setupData} setSetupData={setSetupData} handleSaveSetup={handleSaveSetup} handleFactoryReset={handleFactoryReset} loading={loading} user={user} handleLogout={handleLogout} />}

             {/* --- VIEW: REPORTS GENERATOR --- */}
             {view === 'reports' && (
               <div style={{ padding: '0 15px' }}>
                 
                 <div className="no-print" style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ccc', marginBottom: '20px' }}>
                   <h4 style={{ margin: '0 0 15px 0', color: '#0047a0', textAlign: 'center' }}>Report Generator</h4>
                   
                   <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                     <button onClick={() => {setReportType('class'); setReportData(null); setStudentReportData(null);}} style={{ flex: 1, padding: '8px', fontSize: '12px', fontWeight: 'bold', borderRadius: '5px', border: '1px solid #0047a0', backgroundColor: reportType === 'class' ? '#0047a0' : '#fff', color: reportType === 'class' ? '#fff' : '#0047a0', cursor: 'pointer' }}>Class View</button>
                     <button onClick={() => {setReportType('student'); setReportData(null); setStudentReportData(null);}} style={{ flex: 1, padding: '8px', fontSize: '12px', fontWeight: 'bold', borderRadius: '5px', border: '1px solid #0047a0', backgroundColor: reportType === 'student' ? '#0047a0' : '#fff', color: reportType === 'student' ? '#fff' : '#0047a0', cursor: 'pointer' }}>Student View</button>
                     <button onClick={() => {setReportType('sf2'); setReportData(null); setStudentReportData(null);}} style={{ flex: 1, padding: '8px', fontSize: '12px', fontWeight: 'bold', borderRadius: '5px', border: '1px solid #27ae60', backgroundColor: reportType === 'sf2' ? '#27ae60' : '#fff', color: reportType === 'sf2' ? '#fff' : '#27ae60', cursor: 'pointer' }}>SF 2 (Att.)</button>
                   </div>

                   <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                     
                     {/* Dynamic Selector based on Report Type */}
                     {reportType === 'sf2' ? (
                        <select style={{...inputStyle, flex: 1, minWidth: 0, marginBottom: 0}} value={reportMonth} onChange={e => setReportMonth(e.target.value)}>
                           {monthOptions.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
                        </select>
                     ) : (
                        <select style={{...inputStyle, flex: 1, minWidth: 0, marginBottom: 0}} value={reportQuarter} onChange={e => setReportQuarter(e.target.value)}>
                          {['Q1','Q2','Q3','FINAL RATING'].map(q => <option key={q} value={q}>{q}</option>)}
                        </select>
                     )}
                     
                     <select style={{...inputStyle, flex: 2, minWidth: 0, marginBottom: 0}} value={reportSubject} onChange={e => {setReportSubject(e.target.value); setReportStudent('');}}>
                       <option value="">-- Select Subject / Class --</option>
                       {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                     </select>
                     
                     {reportType === 'student' && (
                       <select style={{...inputStyle, flex: 2, minWidth: 0, marginBottom: 0}} value={reportStudent} onChange={e => setReportStudent(e.target.value)}>
                         <option value="">-- Select Student --</option>
                         {getStudentListForSubject(reportSubject).filter(s => !isHeader(s)).map((st, i) => <option key={i} value={st}>{st}</option>)}
                       </select>
                     )}
                   </div>

                   <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                     <button onClick={generateReport} style={{...btnStyle(reportType === 'sf2' ? '#27ae60' : '#2980b9'), flex: 1, marginBottom: 0}}>
                       {loading ? "Calculating..." : "📊 Generate Report"}
                     </button>
                     {(reportData || studentReportData) && (
                       <button onClick={handlePrintReport} style={{...btnStyle('#27ae60'), flex: 1, marginBottom: 0}}>
                         🖨️ Print Report
                       </button>
                     )}
                   </div>
                 </div>

                 {/* --- NEW UI: SF 2 REPORT TABLE --- */}
                 {reportType === 'sf2' && reportData && reportData.type === 'sf2' && (
                   <div style={{ backgroundColor: 'white', padding: '5px', borderRadius: '5px', border: '1px solid #ccc', overflowX: 'auto' }}>
                     <div style={{ textAlign: 'center', marginBottom: '10px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
                       <h2 style={{ margin: '0 0 5px 0', fontSize: '16px', textTransform: 'uppercase' }}>SCHOOL FORM 2 (SF2)</h2>
                       <div style={{ fontSize: '13px', margin: '3px 0', fontWeight: 'bold' }}>Daily Attendance Report of Learners</div>
                       <div style={{ fontSize: '12px', marginTop: '5px' }}>{setupData.schoolName}</div>
                       <div style={{ fontSize: '12px' }}>For the Month of: <strong>{monthOptions.find(m => m.val === reportMonth)?.label} {setupData.schoolYear.includes('-') ? setupData.schoolYear.split('-')[parseInt(reportMonth) > 5 ? 0 : 1] : setupData.schoolYear}</strong></div>
                       <div style={{ fontSize: '12px' }}>Class: <strong>{reportSubject}</strong></div>
                     </div>

                     <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'center' }}>
                       <thead>
                         <tr style={{ backgroundColor: '#f2f2f2' }}>
                           <th style={{ padding: '4px', border: '1px solid #ccc', textAlign: 'left', minWidth: '90px' }}>Learner's Name</th>
                           {reportData.uniqueDates.map((d, i) => (
                              <th key={i} style={{ padding: '2px', border: '1px solid #ccc', width: '18px', fontWeight: 'bold' }}>{d.split('-')[2]}</th>
                           ))}
                           <th style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#e8f8f5', width: '30px' }}>Pres.</th>
                           <th style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#fdedec', width: '30px' }}>Abs.</th>
                           <th style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#fef9e7', width: '30px' }}>Tardy</th>
                         </tr>
                       </thead>
                       <tbody>
                          {getStudentListForSubject(reportSubject).map((st, idx) => {
                             if (isHeader(st)) return <tr key={idx}><td colSpan={reportData.uniqueDates.length + 4} style={{ padding: '4px', backgroundColor: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? '#0047a0' : '#e0e0e0', color: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? '#fff' : '#000', fontWeight: 'bold', textAlign: 'left' }}>{st}</td></tr>;
                             
                             const stData = reportData.summary[st];
                             if (!stData) return null;

                             return (
                               <tr key={idx}>
                                 <td style={{ padding: '4px', border: '1px solid #ccc', textAlign: 'left', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>{st}</td>
                                 
                                 {reportData.uniqueDates.map((d, i) => (
                                    <td key={i} style={{ padding: '2px', border: '1px solid #ccc', color: stData.daily[d] === 'A' ? '#e74c3c' : stData.daily[d] === 'L' ? '#f1c40f' : '#27ae60', fontWeight: 'bold' }}>
                                       {stData.daily[d]}
                                    </td>
                                 ))}
                                 
                                 <td style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#e8f8f5', fontWeight: 'bold' }}>{stData.present}</td>
                                 <td style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#fdedec', fontWeight: 'bold', color: stData.absent > 0 ? '#c0392b' : '#333' }}>{stData.absent}</td>
                                 <td style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#fef9e7', fontWeight: 'bold', color: stData.tardy > 0 ? '#d68910' : '#333' }}>{stData.tardy}</td>
                               </tr>
                             )
                          })}
                       </tbody>
                     </table>
                   </div>
                 )}

                 {/* CLASS GRADES REPORT TABLE */}
                 {reportType === 'class' && reportData && (
                   <div style={{ backgroundColor: 'white', padding: '5px', borderRadius: '5px', border: '1px solid #ccc', overflowX: 'auto' }}>
                     <div style={{ textAlign: 'center', marginBottom: '10px', borderBottom: '2px solid #000', paddingBottom: '10px' }}>
                       <h2 style={{ margin: '0 0 10px 0', fontSize: '16px', textTransform: 'uppercase' }}>{setupData.schoolName || "SCHOOL NAME"}</h2>
                       <div style={{ fontSize: '13px', margin: '3px 0', fontWeight: 'bold' }}>Subject: {reportSubject}</div>
                       <div style={{ fontSize: '13px', margin: '3px 0' }}>{reportQuarter === 'FINAL RATING' ? 'YEAR-END SUMMARY' : `Quarter ${reportQuarter}`}</div>
                       <div style={{ fontSize: '12px', marginTop: '3px' }}>Teacher: {setupData.teacherName}</div>
                     </div>

                     {reportQuarter === 'FINAL RATING' ? (
                       <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', textAlign: 'center' }}>
                         <thead>
                           <tr style={{ backgroundColor: '#f2f2f2' }}>
                             <th style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'left' }}>Student Name</th>
                             <th style={{ padding: '6px', border: '1px solid #ccc' }}>Quarter 1</th><th style={{ padding: '6px', border: '1px solid #ccc' }}>Quarter 2</th><th style={{ padding: '6px', border: '1px solid #ccc' }}>Quarter 3</th>
                             <th style={{ padding: '6px', border: '1px solid #000', backgroundColor: '#d4efdf' }}>FINAL RATING</th>
                           </tr>
                         </thead>
                         <tbody>
                            {getStudentListForSubject(reportSubject).map((st, idx) => {
                               if (isHeader(st)) return <tr key={idx}><td colSpan="5" style={{ padding: '4px', backgroundColor: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? '#0047a0' : '#e0e0e0', color: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? '#fff' : '#000', fontWeight: 'bold', textAlign: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? 'center' : 'left' }}>{st}</td></tr>;
                               const stData = reportData[st]; if (!stData) return null;
                               const q1 = getQuarterGradeStr(stData.Q1); const q2 = getQuarterGradeStr(stData.Q2); const q3 = getQuarterGradeStr(stData.Q3);
                               let finalAvg = '-'; if (q1 && q2 && q3) finalAvg = ((parseFloat(q1) + parseFloat(q2) + parseFloat(q3)) / 3).toFixed(2);
                               return (
                                 <tr key={idx}>
                                   <td style={{ padding: '4px', border: '1px solid #ccc', textAlign: 'left', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>{st}</td>
                                   <td style={{ padding: '4px', border: '1px solid #ccc' }}>{q1 || '-'}</td><td style={{ padding: '4px', border: '1px solid #ccc' }}>{q2 || '-'}</td><td style={{ padding: '4px', border: '1px solid #ccc' }}>{q3 || '-'}</td>
                                   <td style={{ padding: '4px', border: '1px solid #000', backgroundColor: '#e8f8f5', fontWeight: 'bold', fontSize: '12px' }}>{finalAvg}</td>
                                 </tr>
                               )
                            })}
                         </tbody>
                       </table>
                     ) : (
                       <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'center' }}>
                         <thead>
                           <tr style={{ backgroundColor: '#f2f2f2' }}>
                             <th style={{ padding: '4px 2px', border: '1px solid #ccc', textAlign: 'left', minWidth: '70px', maxWidth: '85px', fontSize: '9px' }}>Student Name</th>
                             <th style={{ padding: '2px 1px', border: '1px solid #ccc', lineHeight: '1.1' }}><div style={{ fontSize: '10px', fontWeight: 'bold' }}>WW</div><div style={{ fontSize: '6.5px', letterSpacing: '-0.5px' }}>SCORE</div></th>
                             <th style={{ padding: '2px 1px', border: '1px solid #ccc', backgroundColor: '#f9f9f9', lineHeight: '1.1' }}><div style={{ fontSize: '10px', fontWeight: 'bold' }}>WW</div><div style={{ fontSize: '6.5px', letterSpacing: '-0.5px' }}>HPS</div></th>
                             <th style={{ padding: '2px 1px', border: '1px solid #ccc', lineHeight: '1.1' }}><div style={{ fontSize: '10px', fontWeight: 'bold' }}>PT</div><div style={{ fontSize: '6.5px', letterSpacing: '-0.5px' }}>SCORE</div></th>
                             <th style={{ padding: '2px 1px', border: '1px solid #ccc', backgroundColor: '#f9f9f9', lineHeight: '1.1' }}><div style={{ fontSize: '10px', fontWeight: 'bold' }}>PT</div><div style={{ fontSize: '6.5px', letterSpacing: '-0.5px' }}>HPS</div></th>
                             <th style={{ padding: '2px 1px', border: '1px solid #ccc', lineHeight: '1.1' }}><div style={{ fontSize: '10px', fontWeight: 'bold' }}>QA</div><div style={{ fontSize: '6.5px', letterSpacing: '-0.5px' }}>SCORE</div></th>
                             <th style={{ padding: '2px 1px', border: '1px solid #ccc', backgroundColor: '#f9f9f9', lineHeight: '1.1' }}><div style={{ fontSize: '10px', fontWeight: 'bold' }}>QA</div><div style={{ fontSize: '6.5px', letterSpacing: '-0.5px' }}>HPS</div></th>
                             <th style={{ padding: '4px 1px', border: '1px solid #000', fontSize: '9px', backgroundColor: '#d4efdf', fontWeight: 'bold' }}>GRADE</th>
                           </tr>
                         </thead>
                         <tbody>
                           {getStudentListForSubject(reportSubject).map((st, idx) => {
                             if (isHeader(st)) return <tr key={idx}><td colSpan="8" style={{ padding: '4px 2px', backgroundColor: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? '#0047a0' : '#e0e0e0', color: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? '#fff' : '#000', fontWeight: 'bold', textAlign: st.startsWith('GRADE ') || st === 'ALL STUDENTS' ? 'center' : 'left' }}>{st}</td></tr>;
                             const stData = reportData[st]; if (!stData) return null;
                             const hasQA = stData.qaHps > 0; let quarterGrade = '-';
                             if (hasQA) {
                               const wwNum = stData.wwHps > 0 ? (stData.wwScore / stData.wwHps) * 40 : 0; const ptNum = stData.ptHps > 0 ? (stData.ptScore / stData.ptHps) * 40 : 0; const qaNum = (stData.qaScore / stData.qaHps) * 20;
                               quarterGrade = (wwNum + ptNum + qaNum).toFixed(2);
                             }
                             return (
                               <tr key={idx}>
                                 <td style={{ padding: '3px 2px', border: '1px solid #ccc', textAlign: 'left', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '85px' }}>{st}</td>
                                 <td style={{ padding: '3px 2px', border: '1px solid #ccc' }}>{stData.wwScore || '-'}</td><td style={{ padding: '3px 2px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}>{stData.wwHps || '-'}</td>
                                 <td style={{ padding: '3px 2px', border: '1px solid #ccc' }}>{stData.ptScore || '-'}</td><td style={{ padding: '3px 2px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}>{stData.ptHps || '-'}</td>
                                 <td style={{ padding: '3px 2px', border: '1px solid #ccc' }}>{stData.qaScore || '-'}</td><td style={{ padding: '3px 2px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}>{stData.qaHps || '-'}</td>
                                 <td style={{ padding: '3px 2px', border: '1px solid #000', backgroundColor: '#e8f8f5', fontWeight: 'bold', fontSize: '10px' }}>{quarterGrade}</td>
                               </tr>
                             );
                           })}
                         </tbody>
                       </table>
                     )}
                   </div>
                 )}

                 {/* --- NEW UI: STUDENT REPORT CARD TABLE --- */}
                 {reportType === 'student' && studentReportData && (
                   <div className="print-container" style={{ backgroundColor: 'white', padding: '30px', color: 'black', width: '100%', boxSizing: 'border-box' }}>
                     
                     {/* Official Document Header (Centered) */}
                     <div style={{ textAlign: 'center', fontSize: '15px' }}>
                       <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                         {setupData.schoolName || "SCHOOL NAME"}
                       </h2>
                       <div style={{ margin: '5px 0', fontSize: '16px' }}>
                         {reportQuarter === 'FINAL RATING' ? 'YEAR-END SUMMARY' : `QUARTER ${reportQuarter}`}
                       </div>
                       <div style={{ margin: '5px 0', fontSize: '16px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                         {reportSubject.includes("- GR") ? "GRADE " + reportSubject.split("- GR")[1].trim() : reportSubject}
                       </div>
                     </div>

                     {/* A good space between Header and Student Name */}
                     <div style={{ height: '40px' }}></div>

                     {/* Student Name Section (Left Justified & Bold) */}
                     <div style={{ marginBottom: '20px', textAlign: 'left', fontSize: '16px' }}>
                       Student Name: <span style={{ fontWeight: 'bold', fontSize: '18px', marginLeft: '10px', textTransform: 'uppercase' }}>{reportStudent}</span>
                     </div>

                     {/* Grades Grid */}
                     <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                       <thead>
                         <tr style={{ backgroundColor: '#f2f2f2' }}>
                           <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left' }}>SUBJECT</th>
                           {reportQuarter === 'FINAL RATING' ? (
                             <>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '15%' }}>QUARTER 1</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '15%' }}>QUARTER 2</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '15%' }}>QUARTER 3</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '15%' }}>FINAL RATING</th>
                             </>
                           ) : (
                             <>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>WW SCORE</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>WW HPS</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>PT SCORE</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>PT HPS</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>QA SCORE</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>QA HPS</th>
                               <th style={{ border: '1px solid #000', padding: '6px', width: '10%' }}>QUARTER GRADE</th>
                             </>
                           )}
                         </tr>
                       </thead>
                       <tbody>
                         {Object.keys(studentReportData).map(subject => {
                           const data = studentReportData[subject];
                           if (!data) return null;

                           // Clean the subject name (e.g., "MATH - GR 2 APIAG" becomes just "MATH")
                           const displaySubject = subject.includes("-") ? subject.split("-")[0].trim() : subject;
                           
                           if (reportQuarter === 'FINAL RATING') {
                              const q1 = getQuarterGradeStr(data.Q1); const q2 = getQuarterGradeStr(data.Q2); const q3 = getQuarterGradeStr(data.Q3);
                              let finalAvg = '-'; if (q1 && q2 && q3) finalAvg = ((parseFloat(q1) + parseFloat(q2) + parseFloat(q3)) / 3).toFixed(2);
                              return (
                                <tr key={subject}>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>{displaySubject}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{q1 || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{q2 || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{q3 || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e8f8f5' }}>{finalAvg}</td>
                                </tr>
                              );
                           }

                           const hasQA = data.qaHps > 0;
                           let quarterGrade = '-';
                           if (hasQA) {
                             const wwNum = data.wwHps > 0 ? (data.wwScore / data.wwHps) * 40 : 0;
                             const ptNum = data.ptHps > 0 ? (data.ptScore / data.ptHps) * 40 : 0;
                             const qaNum = (data.qaScore / data.qaHps) * 20;
                             quarterGrade = (wwNum + ptNum + qaNum).toFixed(2);
                           }

                           return (
                             <tr key={subject}>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>{displaySubject}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{data.wwScore || ''}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: '#f9f9f9' }}>{data.wwHps || ''}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{data.ptScore || ''}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: '#f9f9f9' }}>{data.ptHps || ''}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{data.qaScore || ''}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: '#f9f9f9' }}>{data.qaHps || ''}</td>
                               <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#d4efdf' }}>{quarterGrade}</td>
                             </tr>
                           );
                         })}
                       </tbody>
                     </table>

                     {/* Teacher Signature Block (Bottom Left, Tall Box for Signature) */}
                     <div style={{ marginTop: '80px', display: 'flex', flexDirection: 'column', width: '250px', float: 'left' }}>
                       <div style={{ borderBottom: '1px solid #000', height: '60px' }}></div>
                       <div style={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'center', marginTop: '8px', textTransform: 'uppercase' }}>
                         {setupData.teacherName || "TEACHER NAME"}
                       </div>
                       <div style={{ fontSize: '12px', textAlign: 'center', color: '#555' }}>Class Adviser</div>
                     </div>
                     <div style={{ clear: 'both' }}></div>

                   </div>
                 )}
               </div>
             )}
           </div>
        )}

        <div className="no-print" style={{ width: '100%', backgroundColor: '#fff', borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'space-around', padding: '10px 0', zIndex: 1000, height: '60px', flexShrink: 0, boxSizing: 'border-box', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)' }}>
          <div onClick={goHome} style={{ textAlign: 'center', cursor: 'pointer', flex: 1 }}><div style={{ fontSize: '24px', lineHeight: '1' }}>🏠</div><div style={{ fontSize: '10px', fontWeight: 'bold', color: view === 'menu' ? '#0047a0' : '#555' }}>Home</div></div>
          <div onClick={() => setView('subjects')} style={{ textAlign: 'center', cursor: 'pointer', flex: 1, borderLeft: '1px solid #eee' }}><div style={{ fontSize: '24px', lineHeight: '1' }}>📚</div><div style={{ fontSize: '10px', fontWeight: 'bold', color: view === 'subjects' ? '#0047a0' : '#555' }}>Subjects</div></div>
          <div onClick={() => setView('register')} style={{ textAlign: 'center', cursor: 'pointer', flex: 1, borderLeft: '1px solid #eee' }}><div style={{ fontSize: '24px', lineHeight: '1' }}>👥</div><div style={{ fontSize: '10px', fontWeight: 'bold', color: view === 'register' ? '#0047a0' : '#555' }}>Students</div></div>
          <div onClick={() => setView('reports')} style={{ textAlign: 'center', cursor: 'pointer', flex: 1, borderLeft: '1px solid #eee' }}><div style={{ fontSize: '24px', lineHeight: '1' }}>📊</div><div style={{ fontSize: '10px', fontWeight: 'bold', color: view === 'reports' ? '#0047a0' : '#555' }}>Reports</div></div>
          <div onClick={() => setView('setup')} style={{ textAlign: 'center', cursor: 'pointer', flex: 1, borderLeft: '1px solid #eee' }}><div style={{ fontSize: '24px', lineHeight: '1' }}>⚙️</div><div style={{ fontSize: '10px', fontWeight: 'bold', color: view === 'setup' ? '#0047a0' : '#555' }}>Settings</div></div>
        </div>

      </div>
    </>
  );
}

export default MainApp;